import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getBiomarkerCenterProfile,
  fetchBiomarkerPdfBlobUrl,
  BiomarkerModalityRow,
} from '../../services/api';

const Root = styled.div`
  position: relative;
  min-height: min(480px, 90vh);
  padding: clamp(12px, 2vw, 24px) clamp(8px, 2vw, 16px) clamp(24px, 4vw, 48px);
  background: radial-gradient(ellipse at center, #f0f9ff 0%, #f9fafb 45%, #e5e7eb 100%);
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-sizing: border-box;
`;

const PatientIdCorner = styled.div`
  position: absolute;
  top: clamp(8px, 1.2vw, 14px);
  left: clamp(8px, 1.2vw, 14px);
  z-index: 5;
  max-width: min(100%, calc(100% - 16px));
  font-size: clamp(0.62rem, 0.95vw, 0.8125rem);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #334155;
  line-height: 1.35;
  word-break: break-all;
  pointer-events: none;
`;

const PatientIdLabel = styled.span`
  color: #64748b;
  margin-right: 0.4em;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85em;
  letter-spacing: 0.03em;
`;

const LoadingHint = styled.p`
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
  color: #64748b;
`;

const EmptyHint = styled.p`
  margin: 0;
  text-align: center;
  padding: 48px 16px;
  font-size: 0.875rem;
  color: #64748b;
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  max-width: none;
  /* Grows with viewport: no fixed pixel ceiling — scales with width and height */
  height: max(260px, min(52vw, 72vh));
  margin: 0 auto;
  box-sizing: border-box;
`;

const LineSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const HumanWrap = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  width: clamp(72px, 15vw, min(320px, 42vh));
  height: clamp(132px, 28vw, min(440px, 78vh));
  max-height: 48%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HumanFigureImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
`;

const ModCard = styled.div<{ $angle: number; $radius: number }>`
  position: absolute;
  left: ${(p) => 50 + Math.cos(p.$angle) * p.$radius}%;
  top: ${(p) => 50 + Math.sin(p.$angle) * p.$radius}%;
  transform: translate(-50%, -50%);
  z-index: 3;
  width: clamp(104px, 20vw, min(480px, 34vw));
  max-width: 46%;
  padding: clamp(6px, 1.2vw, 20px) clamp(8px, 1.4vw, 22px);
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #cbd5e1;
  border-radius: clamp(10px, 1.5vw, 20px);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, z-index 0s;
  cursor: default;
  box-sizing: border-box;
  &:hover {
    transform: translate(-50%, -50%) scale(1.08);
    box-shadow: 0 16px 40px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(15, 23, 42, 0.1);
    z-index: 10;
  }
`;

const CardTitle = styled.div`
  font-size: clamp(0.62rem, 1.15vw, 1.05rem);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #3b82f6;
  margin-bottom: clamp(4px, 0.8vw, 12px);
`;

const MetricLine = styled.div`
  font-size: clamp(0.58rem, 1.05vw, 0.95rem);
  color: #334155;
  line-height: 1.35;
  margin-bottom: 3px;
  word-break: break-word;
`;

const GiMapValue = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: #0f172a;
`;

const GiMapUnit = styled.span`
  font-size: 0.92em;
  font-weight: 400;
  color: #64748b;
  margin-left: 0.35em;
`;

const PreviewBtn = styled.button`
  margin-top: clamp(6px, 1vw, 10px);
  width: 100%;
  padding: clamp(4px, 0.8vw, 10px) clamp(6px, 1vw, 14px);
  font-size: clamp(0.62rem, 1.05vw, 0.95rem);
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Err = styled.div`
  color: #b91c1c;
  padding: 16px;
  text-align: center;
`;

/** ``public/man.svg`` / ``woman.svg``: male → man; female or unknown → woman (wireframe preview). */
const PUBLIC = process.env.PUBLIC_URL || '';
const MAN_SVG = `${PUBLIC}/man.svg`;
const WOMAN_SVG = `${PUBLIC}/woman.svg`;

function humanFigureSrc(gender: string) {
  return gender === 'male' ? MAN_SVG : WOMAN_SVG;
}

function HumanSilhouette({ gender }: { gender: string }) {
  const preferred = humanFigureSrc(gender);
  const [src, setSrc] = useState(preferred);

  useEffect(() => {
    setSrc(humanFigureSrc(gender));
  }, [gender]);

  return (
    <HumanFigureImg
      src={src}
      alt=""
      aria-hidden
      draggable={false}
      onError={() => {
        if (src !== MAN_SVG) {
          setSrc(MAN_SVG);
        }
      }}
    />
  );
}

type Props = {
  /** Receives a blob: URL from fetchBiomarkerPdfBlobUrl (same-origin; no patient name in URL). */
  onOpenPdf: (blobUrl: string) => void | Promise<void>;
};

const MultiBiomarkerProfile: React.FC<Props> = ({ onOpenPdf }) => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [gender, setGender] = useState<string>('unknown');
  const [modalities, setModalities] = useState<BiomarkerModalityRow[]>([]);
  const [patientId, setPatientId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [presignErr, setPresignErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await getBiomarkerCenterProfile();
        if (cancelled) return;
        const d = res.data;
        setPatientId(d.patient_id ?? null);
        if (!d.patient_id || !d.modalities?.length) {
          setModalities([]);
          setGender('unknown');
        } else {
          setGender(d.gender || 'unknown');
          setModalities(d.modalities);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : 'Failed to load profile');
          setPatientId(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onPreview = useCallback(
    async (resourceId: string, hasS3: boolean) => {
      if (!hasS3) return;
      setPresignErr(null);
      setPreviewLoading(resourceId);
      try {
        const blobUrl = await fetchBiomarkerPdfBlobUrl(resourceId);
        await onOpenPdf(blobUrl);
      } catch (e: unknown) {
        setPresignErr(e instanceof Error ? e.message : 'Could not open PDF');
      } finally {
        setPreviewLoading(null);
      }
    },
    [onOpenPdf]
  );

  if (loading) {
    return (
      <Root>
        <LoadingHint>Loading…</LoadingHint>
      </Root>
    );
  }

  if (err && !modalities.length) {
    return (
      <Root>
        <Err>{err}</Err>
      </Root>
    );
  }

  const n = modalities.length || 1;
  const radius = 38;

  return (
    <Root>
      {patientId && (
        <PatientIdCorner aria-label={`Patient ID ${patientId}`}>
          <PatientIdLabel>Patient ID</PatientIdLabel>
          {patientId}
        </PatientIdCorner>
      )}
      {presignErr && <Err style={{ marginBottom: 8 }}>{presignErr}</Err>}

      {modalities.length === 0 ? (
        <EmptyHint>
          No multi-modality patient in your group yet. Load sample data (RDS) to see the richest profile.
        </EmptyHint>
      ) : (
        <Stage>
          <LineSvg viewBox="0 0 100 100" preserveAspectRatio="none">
            {modalities.map((_, i) => {
              const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
              const x2 = 50 + Math.cos(angle) * (radius * 0.92);
              const y2 = 50 + Math.sin(angle) * (radius * 0.92);
              return (
                <line
                  key={`ln-${i}`}
                  x1="50"
                  y1="50"
                  x2={x2}
                  y2={y2}
                  stroke="#94a3b8"
                  strokeWidth="0.35"
                  strokeDasharray="1 0.8"
                  opacity={0.85}
                />
              );
            })}
          </LineSvg>

          <HumanWrap>
            <HumanSilhouette gender={gender} />
          </HumanWrap>

          {modalities.map((m, i) => {
            const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
            const canPreview = Boolean(m.s3_key);
            return (
              <ModCard key={m.resource_id} $angle={angle} $radius={radius}>
                <CardTitle>{m.label}</CardTitle>
                {m.metrics.slice(0, 5).map((row, j) =>
                  m.fusion_key === 'gi_map' ? (
                    <MetricLine key={j}>
                      <strong>{row.label}:</strong>{' '}
                      <GiMapValue>{row.value}</GiMapValue>
                      {row.unit ? <GiMapUnit>{row.unit}</GiMapUnit> : null}
                    </MetricLine>
                  ) : (
                    <MetricLine key={j}>
                      <strong>{row.label}:</strong> {row.value}
                      {row.unit ? ` ${row.unit}` : ''}
                    </MetricLine>
                  )
                )}
                <PreviewBtn
                  type="button"
                  disabled={!canPreview || previewLoading === m.resource_id}
                  onClick={() => onPreview(m.resource_id, canPreview)}
                >
                  {previewLoading === m.resource_id ? 'Opening…' : 'Preview PDF'}
                </PreviewBtn>
              </ModCard>
            );
          })}
        </Stage>
      )}
    </Root>
  );
};

export default MultiBiomarkerProfile;
